import React, { useState } from 'react';
import {
  Container,
  VStack,
  Heading,
  Button,
  useToast,
  Box,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { FiDownload, FiTrash2 } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { exportUserData, deleteUserData } from '../services/gdprService';

const DataExport: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const bg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const secondaryColor = useColorModeValue('gray.600', 'gray.400');

  const handleExport = async (): Promise<void> => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to export your data.',
        status: 'error',
      });
      return;
    }

    setIsExporting(true);
    try {
      const data = await exportUserData(user.uid);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vindie-stream-data-export-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Data exported successfully',
        description: 'Your data has been downloaded.',
        status: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description: error.message || 'Failed to export your data.',
        status: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to delete your data.',
        status: 'error',
      });
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete all your data? This action cannot be undone.'
    );

    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteUserData(user.uid);
      toast({
        title: 'Data deleted successfully',
        description: 'All your data has been deleted. You will be logged out.',
        status: 'success',
      });
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error: any) {
      toast({
        title: 'Deletion failed',
        description: error.message || 'Failed to delete your data.',
        status: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Layout>
      <Container maxW="4xl" py={8}>
        <VStack spacing={6} align="stretch" bg={bg} p={8} borderRadius="lg">
          <Heading size="xl" color={textColor}>
            Data Management (GDPR)
          </Heading>

          <Alert status="info">
            <AlertIcon />
            <AlertDescription>
              You have the right to access, export, and delete your personal data
              in accordance with GDPR regulations.
            </AlertDescription>
          </Alert>

          <Box>
            <Heading size="md" mb={3} color={textColor}>
              Export Your Data
            </Heading>
            <Text color={secondaryColor} mb={4}>
              Download a copy of all your data stored on our platform, including
              your profile, content, comments, likes, subscriptions, and analytics.
            </Text>
            <Button
              leftIcon={<IconWrapper icon={FiDownload} size={16} />}
              colorScheme="purple"
              onClick={handleExport}
              isLoading={isExporting}
              loadingText="Exporting..."
            >
              Export All Data
            </Button>
          </Box>

          <Box>
            <Heading size="md" mb={3} color={textColor}>
              Delete Your Data
            </Heading>
            <Text color={secondaryColor} mb={4}>
              Permanently delete all your data from our platform. This action
              cannot be undone. You will be logged out after deletion.
            </Text>
            <Button
              leftIcon={<IconWrapper icon={FiTrash2} size={16} />}
              colorScheme="red"
              variant="outline"
              onClick={handleDelete}
              isLoading={isDeleting}
              loadingText="Deleting..."
            >
              Delete All Data
            </Button>
          </Box>
        </VStack>
      </Container>
    </Layout>
  );
};

export default DataExport;

